import type { FlowDocument } from "./types";

/**
 * Sets one voice on every spoken prompt in a flow: each `tts` node's own
 * voice/language, and each `gather` node's prompt when that prompt is TTS.
 * Publishing checks every voice a flow names against the tenant's own
 * registered voices, while the shipped samples name a placeholder voice
 * (examples/flows/README.md) — this is how "Save as template" makes a
 * sample publishable in one step. Audio prompts and every other node are
 * left exactly as they were. Returns a new document; never mutates.
 */
export function applyVoice(document: FlowDocument, voice: string, language: string): FlowDocument {
  return {
    ...document,
    nodes: document.nodes.map((node) => {
      const config = node.config && typeof node.config === "object" ? (node.config as Record<string, unknown>) : null;
      if (!config) return node;
      if (node.type === "tts") {
        return { ...node, config: { ...config, voice, language } };
      }
      if (node.type === "gather") {
        const prompt = config.prompt && typeof config.prompt === "object" ? (config.prompt as Record<string, unknown>) : null;
        if (prompt?.kind === "tts") {
          return { ...node, config: { ...config, prompt: { ...prompt, voice, language } } };
        }
      }
      return node;
    }),
  };
}

/** Every distinct voice/language pair the flow's spoken prompts use. */
export function voicesUsed(document: FlowDocument): { voice: string; language: string }[] {
  const seen = new Map<string, { voice: string; language: string }>();
  for (const node of document.nodes) {
    const config = node.config && typeof node.config === "object" ? (node.config as Record<string, unknown>) : {};
    const source =
      node.type === "tts"
        ? config
        : node.type === "gather" && config.prompt && typeof config.prompt === "object"
          ? (config.prompt as Record<string, unknown>)
          : null;
    if (!source || (node.type === "gather" && source.kind !== "tts")) continue;
    if (typeof source.voice === "string" && typeof source.language === "string") {
      seen.set(`${source.voice}\u0000${source.language}`, { voice: source.voice, language: source.language });
    }
  }
  return [...seen.values()];
}
